import {
  Component,
  ChangeDetectionStrategy,
  EventEmitter,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { RegistrationsComponent } from '@dpr-details/models/component';
import { DprDetailsGridBaseComponent } from '@dpr-details/models/grid';
import {
  getFormulaInputColumnIds,
  getTableOfColumnFormulas,
  isTableOfColumnItemCarryOverEnabled,
  isTableOfColumnItemDerived,
  getDerivedColumnOptions,
  normalizeAnchorValue,
} from '@dpr-details/utils';
import {
  DateDisplayComponent,
  DateEditComponent,
  NumericEditComponent,
  SelectDisplayComponent,
  SelectEditComponent,
  TextDisplayComponent,
  TextEditComponent,
} from '@grid/components';
import { GridAlignment, GridFooterType } from '@grid/enums';
import {
  GridOptions,
  GridColumn,
  GridSync,
  HeaderOptions,
  SelectionItem,
} from '@grid/models';
import { selectTooltipMessage } from '@grid/utils/select';
import { StaticMessage } from '@shared/enums';
import { ExtendedDataType } from '@shared/models';
import { DprTableOfColumnDetail } from '@shared/models/dpr';
import {
  Constants,
  getStaticMessage,
  isDataTypeSummarizable,
  isDecimalDataType,
  buildBooleanValues,
  isNullOrUndefined,
  getFormulaDisplayExpression,
} from '@shared/utils';
import { Constants as GridConstants } from '@grid/utils/constants';
import {
  GridColumns,
  GridDefaultLabels,
  getDecimalCellDisplay,
} from '@shared/utils/grid';
import {
  TableOfColumnItem,
  TableOfColumnItemWithValues,
  TableOfColumnWithData,
} from '@shared/models/project-setup';

@Component({
  selector: 'app-dpr-table-of-columns-registrations-grid',
  templateUrl: './dpr-table-of-columns-registrations-grid.component.html',
  styleUrls: ['./dpr-table-of-columns-registrations-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprTableOfColumnsRegistrationsGridComponent
  extends DprDetailsGridBaseComponent<DprTableOfColumnDetail>
  implements RegistrationsComponent<DprTableOfColumnDetail>, OnChanges
{
  @Output() anchorEdited = new EventEmitter<DprTableOfColumnDetail>();
  @Output() formulaInputEdited = new EventEmitter<DprTableOfColumnDetail>();

  @ViewChild('timeComponent', { static: true })
  timeComponent: TemplateRef<any>;

  anchorHeaderTooltipMessage: string = $localize`:@@dprDetails.dprTableOfColumnsRegistrationsGrid.colAnchorTooltip:Anchor column`;

  constructor(protected activatedRoute: ActivatedRoute) {
    super();
  }

  //#region Getters

  get tableOfColumnId(): number {
    return +this.activatedRoute.parent.snapshot.params.tableOfColumnId;
  }

  get tableOfColumnItems(): TableOfColumnItemWithValues[] {
    return this.tableOfColumn?.tableOfColumnItems;
  }

  get summaryRowLabel(): string {
    return this.tableOfColumn?.summaryLabel;
  }

  get enableAutoFill(): boolean {
    return this.tableOfColumn?.enableAutofill;
  }

  private get tableOfColumn(): TableOfColumnWithData {
    return this.selectedSubproject.data.tableOfColumns.find(
      (x) => x.id === this.tableOfColumnId
    );
  }

  private get hasAnchorColumn(): boolean {
    return this.tableOfColumn?.anchorColumn?.anchorColumnId != null;
  }

  //#endregion

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.predefinedChanges?.currentValue) {
      this.gridComponent?.api?.setPredefinedChanges?.(this.predefinedChanges);
    }

    if (changes.selectedSubproject?.currentValue && this.labeling) {
      this.gridColumns$.next(this.getGridColumns());
    }

    if (changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
    }
  }

  /**
   * Accumulated and formula items are excluded: their value is computed rather than
   * entered, so counting them would make every row look filled in and stop an
   * emptied row from being removed.
   */
  isRowEmpty(data: DprTableOfColumnDetail): boolean {
    return this.tableOfColumnItems
      ?.filter((item) => !isTableOfColumnItemDerived(item))
      .every((item) => data[item.id] == null);
  }

  protected getGridOptions(): GridOptions<DprTableOfColumnDetail> {
    return {
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: this.canEdit,
        keepAutogeneratedIds: true,
      },
      changesConfig: {
        //When a toc row is added in online mode and then values are emptied in offline mode
        //The row was not deleted. Due to the fact that the added row was not treated as a predefined change,
        //the way it is treated if the row was added in offline mode. This way if the row is empty we add it to the deleted
        //Array so that it's removed from the UI and also removed when the user switches back to online
        changesAdapter: (changes: GridSync<DprTableOfColumnDetail>) => {
          changes.deletedIds = [
            ...changes.deletedIds,
            ...changes.updatedList
              .filter((x) => this.isRowEmpty(x))
              .map((x) => x.id),
          ];
          changes.updatedList = changes.updatedList.filter(
            (x) => !this.isRowEmpty(x)
          );

          const autoFillableItemIds = this.getAutoFillableItemIds();

          changes.updatedList.forEach((change) => {
            autoFillableItemIds.forEach((itemId) => {
              const key = itemId.toString();

              if (isNullOrUndefined(change[key])) {
                change[key] = 0;
              }
            });
          });
          changes.addedList.forEach((change) => {
            autoFillableItemIds.forEach((itemId) => {
              const key = itemId.toString();

              if (isNullOrUndefined(change[key])) {
                change[key] = 0;
              }
            });
          });
          return changes;
        },
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: this.canEdit,
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: this.canRowBeDeleted.bind(this),
        allowSelection: this.canRowBeDeleted.bind(this),
        isEmpty: this.isRowEmpty.bind(this),
      },
      selectionConfig: {
        allowSelection: this.canEdit,
      },
      sortConfig: {
        allowSorting: false,
      },
      sequenceConfig: {
        dynamicWidth: true,
        maxWidth: 300,
      },
      footerConfig: {
        displayFooter: this.shouldDisplaySummaryRow(this.tableOfColumnItems),
        label: !String.isNullOrWhiteSpace(this.summaryRowLabel)
          ? this.summaryRowLabel
          : $localize`:@@dprDetails.dprTableOfColumnsRegistrations.lblSummary:Summary`,
      },
      events: {
        onGridRegistered: this.onGridRegistered.bind(this),
      },
    };
  }

  protected getGridColumns(): GridColumn<DprTableOfColumnDetail>[] {
    return [...this.getDprColumns(), GridColumns.delete];
  }

  protected getDprColumns(): GridColumn<DprTableOfColumnDetail>[] {
    const columns: GridColumn<DprTableOfColumnDetail>[] = [];
    const tableOfColumn = this.tableOfColumn;
    const formulas = getTableOfColumnFormulas(tableOfColumn);
    const formulaInputIds = getFormulaInputColumnIds(formulas);
    const hasDerivedItems = tableOfColumn?.tableOfColumnItems?.some((item) =>
      isTableOfColumnItemDerived(item)
    );
    // Taken from the project setup, so the anchor handler is attached even when
    // the columns are built before the DPR's values have loaded.
    const anchorColumnId =
      tableOfColumn?.anchorColumn?.anchorColumnId != null
        ? +tableOfColumn.anchorColumn.anchorColumnId
        : undefined;

    this.tableOfColumnItems
      ?.filter(
        (tableOfColumnItem) =>
          tableOfColumnItem.tableOfColumnId === this.tableOfColumnId
      )
      .sort((a, b) => a.priority - b.priority)
      .forEach((item) => {
        const header: HeaderOptions<DprTableOfColumnDetail> = {
          name: `${item.name}`,
        };

        let column: GridColumn<DprTableOfColumnDetail> = {
          columnDef: `${item.id}`,
          header: header,
          minWidth: 80,
          allowSorting: false,
          footer:
            item.includeInSummary && isDataTypeSummarizable(item.dataTypeId)
              ? {
                  type: GridFooterType.Sum,
                  allowPercentage:
                    item.dataTypeId === ExtendedDataType.Percentage,
                  format: isDecimalDataType(item.dataTypeId)
                    ? GridConstants.DefaultValues.decimalDisplayFormat
                    : undefined,
                }
              : { type: GridFooterType.Text, label: '-' },
        };

        switch (item.dataTypeId) {
          case ExtendedDataType.FreeText: {
            column = {
              ...column,
              display: { type: TextDisplayComponent },
              edit: {
                type: TextEditComponent,
                config: {
                  options: {
                    maxLength: Constants.FieldLimits.maxFreeTextDataTypeLimit,
                    allowNullValue: true,
                  },
                },
              },
            };
            break;
          }
          case ExtendedDataType.Number: {
            column = {
              ...column,
              header: {
                ...header,
                alignment: GridAlignment.Right,
              },
              columnAlignment: GridAlignment.Right,
              display: getDecimalCellDisplay(item.dataTypeId),
              edit: {
                type: NumericEditComponent,
                config: {
                  options: {
                    step: 0.01,
                    decimalPrecision: 2,
                    min: Constants.NumericOptions.minTableOfColumns,
                    max: Constants.NumericOptions.maxTableOfColumns,
                  },
                },
                allowEditing: !isTableOfColumnItemCarryOverEnabled(item),
              },
            };
            break;
          }
          case ExtendedDataType.List: {
            const options = item.values
              .filter((v) => v.isActive)
              .map((dataTypeValue) => ({
                id: dataTypeValue.id,
                name: dataTypeValue.value,
              }));
            const isAnchorColumn = item.id === anchorColumnId;

            column = {
              ...column,
              display: {
                type: SelectDisplayComponent,
                config: { options },
              },
              edit: {
                type: SelectEditComponent,
                config: {
                  options: (data: DprTableOfColumnDetail) => {
                    if (data && data[column.columnDef]) {
                      data[column.columnDef] = +data[column.columnDef];
                    }
                    return isAnchorColumn
                      ? this.getAvailableAnchorOptions(
                          options,
                          data,
                          column.columnDef
                        )
                      : options;
                  },
                  includeNoneOption: true,
                  noneOptionName: getStaticMessage(
                    StaticMessage.NoneOptionName
                  ),
                },
                allowEditing: isAnchorColumn
                  ? (data: DprTableOfColumnDetail) =>
                      !this.isAnchorLocked(data, column.columnDef)
                  : true,
              },
              tooltip: {
                message: selectTooltipMessage(options),
              },
            };

            if (isAnchorColumn) {
              column = {
                ...column,
                header: {
                  ...header,
                  icon: 'pin',
                  tooltip: this.anchorHeaderTooltipMessage,
                  customClass: 'anchorColumnHeader',
                },
                // A locked anchor cell looks like the other read-only registration
                // cells: out of the tab order and never rendered as focused.
                tabIndex: (data: DprTableOfColumnDetail) =>
                  this.isAnchorLocked(data, column.columnDef) ? -1 : 0,
                disableFocusedBackground: (data: DprTableOfColumnDetail) =>
                  this.isAnchorLocked(data, column.columnDef),
              };

              if (hasDerivedItems) {
                column = {
                  ...column,
                  // The accumulated cells of the row are read from the previous DPR
                  // through this value, so they have to be resolved again and
                  // repainted whenever it changes.
                  onCellEdited: (data: DprTableOfColumnDetail) =>
                    this.anchorEdited.emit(data),
                  recomputeRowOnChange: true,
                };
              }
            }
            break;
          }
          case ExtendedDataType.Percentage: {
            column = {
              ...column,
              header: {
                ...header,
                alignment: GridAlignment.Right,
              },
              columnAlignment: GridAlignment.Right,
              display: getDecimalCellDisplay(item.dataTypeId),
              edit: {
                type: NumericEditComponent,
                config: {
                  options: {
                    step: 0.01,
                    decimalPrecision: 2,
                    allowPercentage: true,
                    allowSign: false,
                  },
                },
                allowEditing: !isTableOfColumnItemCarryOverEnabled(item),
              },
            };
            break;
          }
          case ExtendedDataType.Date:
            {
              column = {
                ...column,
                header: {
                  ...header,
                  alignment: GridAlignment.Right,
                },
                columnAlignment: GridAlignment.Right,
                minWidth: 100,
                display: { type: DateDisplayComponent },
                edit: {
                  type: DateEditComponent,
                  config: {
                    allowClearOption: true,
                    clearButtonLabel: getStaticMessage(
                      StaticMessage.ClearButton
                    ),
                    timeOptions: {
                      withoutTimeZone: true,
                    },
                  },
                  delegateEndEdit: true,
                },
              };
            }
            break;
          case ExtendedDataType.Datetime:
            {
              column = {
                ...column,
                header: {
                  ...header,
                  alignment: GridAlignment.Right,
                },
                columnAlignment: GridAlignment.Right,
                minWidth: 160,
                display: {
                  type: DateDisplayComponent,
                  config: {
                    format:
                      GridConstants.DefaultValues.dateDisplayFormatWithTime,
                  },
                },
                edit: {
                  type: DateEditComponent,
                  config: {
                    saveButtonLabel: getStaticMessage(StaticMessage.SaveButton),
                    clearButtonLabel: getStaticMessage(
                      StaticMessage.ClearButton
                    ),
                    timeOptions: {
                      allowTimePicker: true,
                      timePickerComponent: this.timeComponent,
                      withoutTimeZone: true,
                    },
                    format:
                      GridConstants.DefaultValues.dateDisplayFormatWithTime,
                  },
                  delegateEndEdit: true,
                },
              };
            }
            break;
          case ExtendedDataType.Boolean: {
            const options = buildBooleanValues(item.booleanTerminologyId).map(
              (v) => ({ id: v.id, name: v.value })
            );

            column = {
              ...column,
              display: {
                type: SelectDisplayComponent,
                config: { options },
              },
              edit: {
                type: SelectEditComponent,
                config: {
                  options: (data: DprTableOfColumnDetail) => {
                    if (data && data[column.columnDef]) {
                      data[column.columnDef] = +data[column.columnDef];
                    }
                    return options;
                  },
                  includeNoneOption: true,
                  noneOptionName: getStaticMessage(
                    StaticMessage.NoneOptionName
                  ),
                },
              },
              tooltip: {
                message: selectTooltipMessage(options),
              },
            };
            break;
          }
          case ExtendedDataType.Formula: {
            column = {
              ...column,
              header: {
                ...header,
                icon: 'function-variant',
                tooltip: getFormulaDisplayExpression(
                  item.formula?.expression,
                  tableOfColumn?.tableOfColumnItems
                ),
                alignment: GridAlignment.Right,
              },
              columnAlignment: GridAlignment.Right,
              display: getDecimalCellDisplay(item.dataTypeId),
            };
            break;
          }
        }

        column = {
          ...column,
          ...getDerivedColumnOptions(isTableOfColumnItemDerived(item)),
        };

        // A carry-over column a formula reads is refilled by the anchor handler,
        // which recalculates the formulas once every refill is in place.
        if (
          formulaInputIds.has(item.id) &&
          item.id !== anchorColumnId &&
          !isTableOfColumnItemCarryOverEnabled(item)
        ) {
          column = {
            ...column,
            onCellEdited: (data: DprTableOfColumnDetail) =>
              this.formulaInputEdited.emit(data),
            recomputeRowOnChange: true,
          };
        }

        columns.push(column);
      });
    return columns;
  }

  private shouldDisplaySummaryRow(
    tableOfColumnItems: TableOfColumnItem[]
  ): boolean {
    return (
      this.tableOfColumn?.summaryEnabled &&
      tableOfColumnItems.some(
        (tableOfColumnItem: TableOfColumnItem) =>
          isDataTypeSummarizable(tableOfColumnItem.dataTypeId) &&
          tableOfColumnItem.includeInSummary
      )
    );
  }

  private getAutoFillableItemIds(): number[] {
    const autoFillableDataTypes = [
      ExtendedDataType.Number,
      ExtendedDataType.Percentage,
    ];

    const itemIds: number[] = [];

    if (!this.enableAutoFill) return itemIds;

    this.tableOfColumnItems.forEach((item) => {
      // An accumulated item is never auto-filled: its value comes from the previous
      // DPR, and a row with no anchor value has none to show.
      if (
        autoFillableDataTypes.includes(item.dataTypeId) &&
        !isTableOfColumnItemCarryOverEnabled(item)
      ) {
        itemIds.push(item.id);
      }
    });

    return itemIds;
  }

  /**
   * The next DPR reads a saved row's values through its anchor option, so deleting
   * the row would break the carry-over history.
   */
  private canRowBeDeleted(data: DprTableOfColumnDetail): boolean {
    return !this.hasAnchorColumn || !this.isSavedRow(data);
  }

  /**
   * The grid adds a row without a table id, and saving it, online or offline,
   * gives it one.
   */
  private isSavedRow(data: DprTableOfColumnDetail): boolean {
    return data.dprTableOfColumnId != null;
  }

  /**
   * Later DPRs read a row's values through its anchor option, so the option is
   * fixed once the row is saved with one. An option given to a saved row in this
   * session stays changeable until it is saved.
   */
  private isAnchorLocked(
    data: DprTableOfColumnDetail,
    columnDef: string
  ): boolean {
    return (
      this.isSavedRow(data) &&
      normalizeAnchorValue(data[columnDef]) !== null &&
      !this.api?.isPropertyChanged(data, columnDef)
    );
  }

  /**
   * Rows are matched across DPRs through their anchor option, so a table holds
   * each option at most once.
   */
  private getAvailableAnchorOptions(
    options: SelectionItem[],
    data: DprTableOfColumnDetail,
    columnDef: string
  ): SelectionItem[] {
    const anchorValuesInUse = new Set(
      this.gridData
        ?.filter((row) => row.id !== data?.id)
        .map((row) => normalizeAnchorValue(row[columnDef]))
    );

    return options.filter(
      (option) => !anchorValuesInUse.has(normalizeAnchorValue(option.id))
    );
  }
}
